/**
 * ClaudeCredentialModal — paste-token form for a Claude credential used to
 * deploy a managed app.
 *
 * CAPTURE-ONLY (2026-06-15 credential-scoping fix): this modal NEVER
 * persists the token to any account-level store. Agent credentials are
 * PER-PROJECT now — the durable home is the project's AI Settings
 * (`secrets.aiOAuthToken` / `secrets.aiApiKey`, managed in Project
 * Settings). This modal exists only to capture a token for a SINGLE deploy
 * when the selected project has no AI credential yet: it validates the
 * shape locally and hands the plaintext back to the caller via
 * `onCapture({ token, mode })`. The caller injects it into the deploy POST
 * body as the vendor-neutral `agentToken` (per-app, ephemeral) and drops it
 * immediately. Nothing is written to the account store or the project row.
 *
 * One field, neutral wording (matches the agents "Deploy — model & key" modal):
 * paste an Anthropic API key (`sk-ant-api03-…`). A Claude Code OAuth token
 * (`sk-ant-oat01-…`) is still ACCEPTED if pasted, but the UI no longer
 * advertises/defaults to it — using a subscription token for an automated
 * agent is the Anthropic Consumer §3(7) path we deliberately stopped promoting.
 *
 * Uses the app's shared <ConfirmModal> (same `mkt-modal` shell as the app
 * deploy/preview modal + every other marketplace dialog) for the chrome.
 *
 * Props: { isOpen, onClose, onCapture({ token, mode }) }  (defaultKind is
 * accepted for back-compat but ignored — there's no longer a kind radio.)
 */

import React, { useEffect, useState } from 'react';
import ConfirmModal from '../molecules/ConfirmModal/ConfirmModal';
import PillButton from '../shared/PillButton';
// Reuse the SAME atomic field + styles as the agents "Deploy — model & key"
// modal so the two credential inputs are identical.
import Field from '../atoms/Field/Field';
import '../molecules/AgentModelFields/AgentModelFields.css';

export default function ClaudeCredentialModal({ isOpen, onClose, onCapture }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setToken(''); setError(''); }
  }, [isOpen]);

  const handleSubmit = () => {
    const trimmed = token.trim();
    if (!trimmed) { setError('A key is required.'); return; }
    // Capture-only: validate the shape locally + hand the plaintext back to
    // the caller. NEVER persisted (not to an account store, not to the
    // project). The caller injects it into the deploy as `agentToken`.
    // The field accepts either format; mode is detected from the value.
    if (!/^sk-ant-(oat01|api03)-/.test(trimmed)) {
      setError('Key must start with sk-ant-api03- (API key) or sk-ant-oat01-.');
      return;
    }
    const mode = /^sk-ant-oat01-/.test(trimmed) ? 'oauth' : 'api';
    onCapture?.({ token: trimmed, mode });
    onClose?.();
  };

  return (
    <ConfirmModal
      open={isOpen}
      onClose={onClose}
      title="Connect Claude"
      ariaLabel="Connect Claude"
      secondaryLabel=""
    >
      <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 0, lineHeight: 1.5 }}>
        Used only for this deploy, stored encrypted on the app — never saved to
        your account. For a reusable one: Project Settings &rarr; AI.
      </p>
      <div className="agent-model-field" style={{ marginTop: 14 }}>
        <label className="apps-modal-field-label" htmlFor="claude-cred-token">
          API key / OAuth token
        </label>
        <Field
          as="input"
          id="claude-cred-token"
          type="text"
          name="zibby-agent-token"
          value={token}
          placeholder="sk-ant-…"
          autoComplete="new-password"
          data-1p-ignore="true"
          data-lpignore="true"
          spellCheck={false}
          onChange={(e) => { setToken(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && token.trim()) { e.preventDefault(); handleSubmit(); } }}
          status={error ? 'error' : null}
          aria-invalid={error ? true : undefined}
        />
        {error ? (
          <p className="agent-model-field-error">{error}</p>
        ) : (
          <p className="agent-model-field-hint">
            Anthropic API key (<code>sk-ant-api03-…</code>). Get one at{' '}
            <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>.
          </p>
        )}
      </div>
      <div style={{ marginTop: 16 }}>
        <PillButton onClick={handleSubmit} disabled={!token.trim()} className="btn-fullwidth">
          Use for this deploy
        </PillButton>
      </div>
    </ConfirmModal>
  );
}
